/**
 * Autosave: every edit is written through the persistence port with no Save
 * control anywhere in the module (spec R8, R45; ADR-002).
 *
 * Writes are debounced so a Display-label keystroke storm does not become a
 * write storm the future API would feel, and the pending write is flushed when
 * the editor unmounts — which is what makes "change the orientation, press back,
 * re-open the card" show the change (spec R30, R35).
 *
 * WHAT AN AUTOSAVE OWES THE USER
 * ------------------------------
 * Removing the Save button removes the user's evidence that their work is safe.
 * Everything below exists to give that evidence back, because the first version
 * of this hook could lose an edit without anybody finding out:
 *
 *   1. **A state, not just an error.** `idle → saving → saved`, with the time of
 *      the last successful write. The header renders it. Without this the only
 *      feedback in the whole feature was a red banner on failure, so "saved" and
 *      "silently not saving" looked identical.
 *
 *   2. **A mounted guard.** The unmount flush used to call `setSaveError` after
 *      unmount — React said so on every test run. That is not only a warning:
 *      the unmount write is the one most likely to matter (the user is leaving),
 *      and its failure had nowhere to go. It was dropped, and the user was told
 *      their edit had saved by the absence of any sign otherwise.
 *
 *   3. **Retry with backoff.** A failed write used to sit there until the user
 *      typed again — and the copy told them to do exactly that. A transient
 *      failure (a locked store, a 503 from the adapter that replaces it) should
 *      not need a human to notice it.
 *
 *   4. **A flush on page hide.** An edit made inside the 500ms debounce window
 *      and followed by a tab close was simply gone. `pagehide` is what fires
 *      reliably on mobile Safari, where `beforeunload` often does not.
 *
 *   5. **One write in flight at a time.** Invisible while the adapter was
 *      synchronous browser storage; the day the HTTP adapter landed
 *      (2026-08-18), rapid typing put two PUTs on the wire in the same second
 *      with the same revision — the server 409'd the loser (watched live).
 *      Worse, the loser's catch used to restore ITS document over any newer
 *      one already queued, so a retry could regress the server while the
 *      editor showed newer content. Now a write launched mid-flight parks in
 *      `pendingRef` and drains when the in-flight write settles, and a failed
 *      write only restores its document if nothing newer is queued — the
 *      newer snapshot contains the older edit anyway (whole-document saves).
 *      A 409 therefore means what it says: ANOTHER writer (a second tab).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { AUTOSAVE_DEBOUNCE_MS, STRINGS } from '../constants';
import { isFieldError } from '../services';
import { ProductCard, ProductCardsRepository } from '../types';

/** What the editor tells the user about the state of their work. */
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Retry schedule for a failed write, in milliseconds.
 *
 * Three attempts over ~7 seconds. Bounded rather than indefinite: past this the
 * failure is not transient, and a hook silently retrying forever is how a
 * broken save looks fine in a dashboard. After the last one the error surfaces
 * and the user's own next edit is what tries again.
 */
const RETRY_DELAYS_MS = [1000, 2000, 4000];

interface UseAutosaveResult {
  /** Copy to surface when the last write failed; null while all is well. */
  saveError: string | null;
  /**
   * The input the server blamed, when it blamed one — today only `"name"`, from
   * the duplicate-card-name rule. Non-null means **retrying cannot help**: the
   * user has to change that field. The indicator uses it to drop its Retry
   * button, which would otherwise offer an action guaranteed to fail.
   */
  saveErrorField: string | null;
  status: SaveStatus;
  /** ISO-8601 time of the last successful write; null until there is one. */
  lastSavedAt: string | null;
  /** True while an edit is debounced, in flight, or waiting on a retry. */
  hasPendingChanges: boolean;
  /** Writes any pending change immediately. */
  flush: () => void;
  /** Re-attempts the write the user was told about. */
  retry: () => void;
}

export const useAutosave = (
  card: ProductCard | null,
  repository: ProductCardsRepository,
  enabled: boolean
): UseAutosaveResult => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveErrorField, setSaveErrorField] = useState<string | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // The document as last handed to the repository, so an unchanged card never
  // triggers a write — opening a card must not bump its Updated At (spec R27).
  const lastSavedRef = useRef<string | null>(null);
  const pendingRef = useRef<ProductCard | null>(null);
  const timerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const repositoryRef = useRef(repository);
  repositoryRef.current = repository;

  /**
   * Whether this hook's component is still on the page.
   *
   * Every `set*` below goes through the guards that read it. The flush on
   * unmount deliberately still *writes* — losing the last edit is the whole
   * thing we are avoiding — it just must not narrate the result to a component
   * that no longer exists.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearTimer = (ref: React.MutableRefObject<number | null>): void => {
    if (ref.current !== null) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  };

  /** True from a save's launch until it settles; the single-flight latch. */
  const inFlightRef = useRef(false);

  const write = useCallback(() => {
    if (inFlightRef.current) {
      // A write is already on the wire. The edit stays in pendingRef and is
      // drained the moment that write settles — starting a second one here is
      // how two same-revision PUTs raced each other live on 2026-08-18.
      return;
    }
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }
    inFlightRef.current = true;
    // Cleared before the attempt, and restored below if it fails — so a write
    // in flight cannot be started twice, and a failed one is still recoverable.
    pendingRef.current = null;
    lastSavedRef.current = JSON.stringify(pending);

    if (mountedRef.current) {
      setStatus('saving');
    }

    // The clock lives here rather than in the reducer, so a mutation stamps
    // Updated At exactly once, at the moment it is persisted.
    const stampedAt = new Date().toISOString();
    repositoryRef.current
      .save({ ...pending, updatedAt: stampedAt })
      .then(() => {
        inFlightRef.current = false;
        retryCountRef.current = 0;
        if (mountedRef.current) {
          setSaveError(null);
          setSaveErrorField(null);
          setStatus('saved');
          setLastSavedAt(stampedAt);
          setHasPendingChanges(pendingRef.current !== null);
        }
        // Drain an edit that arrived while this write was on the wire — its
        // debounce already elapsed, it was only waiting for the latch. Runs
        // even unmounted, for the same reason the unmount flush writes.
        if (pendingRef.current !== null) {
          write();
        }
      })
      .catch((error: unknown) => {
        inFlightRef.current = false;
        // Put the document back so a retry — automatic or manual — has
        // something to write, UNLESS a newer one is already queued: these are
        // whole-document snapshots, so the newer one contains this edit, and
        // restoring the older would regress the server past content the
        // editor is already showing.
        if (pendingRef.current === null) {
          pendingRef.current = pending;
        }
        lastSavedRef.current = null;

        // A refusal naming an input is not transient — the same request fails
        // identically every time. Burning the ladder first would leave the user
        // watching "Saving…" for seven seconds and then reading a generic
        // failure, when the server already said exactly what to change.
        if (isFieldError(error)) {
          retryCountRef.current = 0;
          if (mountedRef.current) {
            setSaveError(error.message);
            setSaveErrorField(error.field);
            setStatus('error');
            setHasPendingChanges(true);
          }
          return;
        }

        const attempt = retryCountRef.current;
        if (attempt < RETRY_DELAYS_MS.length) {
          retryCountRef.current = attempt + 1;
          clearTimer(retryTimerRef);
          retryTimerRef.current = window.setTimeout(write, RETRY_DELAYS_MS[attempt]);
          if (mountedRef.current) {
            setStatus('saving');
            setHasPendingChanges(true);
          }
          return;
        }

        if (!mountedRef.current) {
          return;
        }
        setSaveError(STRINGS.editor.saveFailed);
        setSaveErrorField(null);
        setStatus('error');
        setHasPendingChanges(true);
      });
  }, []);

  const flush = useCallback(() => {
    clearTimer(timerRef);
    write();
  }, [write]);

  const retry = useCallback(() => {
    clearTimer(retryTimerRef);
    retryCountRef.current = 0;
    write();
  }, [write]);

  useEffect(() => {
    if (!enabled || !card) {
      return undefined;
    }

    const serialised = JSON.stringify(card);
    if (lastSavedRef.current === null && pendingRef.current === null) {
      // First sight of the loaded document: nothing to save yet.
      lastSavedRef.current = serialised;
      return undefined;
    }
    if (lastSavedRef.current === serialised) {
      return undefined;
    }

    pendingRef.current = card;
    setHasPendingChanges(true);
    clearTimer(timerRef);
    timerRef.current = window.setTimeout(write, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      clearTimer(timerRef);
    };
  }, [card, enabled, write]);

  // Keep the latest flush reachable from listeners that are registered once.
  const flushRef = useRef(flush);
  flushRef.current = flush;

  /**
   * The tab is going away. An edit made less than `AUTOSAVE_DEBOUNCE_MS` ago is
   * still sitting in the timer, and closing the tab used to drop it.
   *
   * Both events are registered: `pagehide` is the one that fires reliably when
   * mobile Safari freezes a page into the back/forward cache, where
   * `beforeunload` frequently does not.
   */
  useEffect(() => {
    const handleHide = (): void => {
      flushRef.current();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      flushRef.current();

      // Only prompt when we already KNOW the write is failing. Interrupting
      // every navigation with a browser dialog because a save might be in
      // flight is how users learn to dismiss the dialog without reading it —
      // and then it cannot warn them about the one that matters.
      if (pendingRef.current !== null && retryCountRef.current >= RETRY_DELAYS_MS.length) {
        event.preventDefault();
        // Required by the older half of the spec; browsers ignore the text.
        event.returnValue = '';
      }
    };

    window.addEventListener('pagehide', handleHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', handleHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Flush on unmount — leaving the editor must not lose the last edit.
  useEffect(
    () => () => {
      clearTimer(retryTimerRef);
      flushRef.current();
    },
    []
  );

  return {
    saveError,
    saveErrorField,
    status,
    lastSavedAt,
    hasPendingChanges,
    flush,
    retry,
  };
};
